import React, { useRef, useEffect, useMemo } from 'react';
import { SENSOR_FUSION_DATA_LAYERS } from '@/types/shared/navigation-tabs.enums';
import { BaseCard } from '@/components/uiKit/Cards';
import CardTitle from '@/components/uiKit/Cards/CardTitle';

import { transformFilters } from '@/modules/AssetsStudioPage/SensorFusion/config/data-layers-config';
import type { ExpandableLegend, DataLayerWithExpandableLegend } from '@/types/ui/expandable-legend.types';
import { isExpandableLegendEnabled } from '@/config/featureFlags';
import { getExpandableLegendItems, convertItemsToPopoverFormat, mergeExpandableLegendItemsWithVerticalLines } from '@/utils/expandableLegend.utils';
import { useFilterSystem } from '@/modules/AssetsStudioPage/SensorFusion/hooks/use-filter-system';
import { ManagedGraphsSection } from '@/modules/shared/DataLayers/components';

type LegendSyncReturn = {
  mode: 'multi' | 'focus';
  toggleMode: () => void;
  getSavedState?: () => Record<string, Record<string, boolean>> | null;
  clearSavedState?: () => void;
};

export interface DataLayerConfig {
  id: string;
  condition?: () => boolean;
  component: () => React.ReactNode;
}

export const createDataLayersConfig = (props: {
  activeDataLayers: string[];
  isMobileAsset: boolean;
  assetName?: string;
  diagnostics: any[];
  baseTitle?: string;
  from: string;
  to: string;

  spectrumGraphs: any[];
  spectrumAnalysisGraphs: any;

  spectrumManager: any;
  spectrumAnalysisManager: any;

  enhancedQueryParams: any;
  forceDataRefresh: number;

  handleSensorLoadingStateChange: (isLoading: boolean) => void;
  handleOilLoadingStateChange: (isLoading: boolean) => void;
  handleCoolantLoadingStateChange: (isLoading: boolean) => void;

  // loadingStates?: any;

  selectedComponentId?: string | null;
  selectedComponentObj?: any;
  setTriggerSensorReset: (value: boolean) => void;

  config?: any;
  legendItemStates?: Record<string, boolean>;
  toggleLegendItem?: (itemId: string, enabled: boolean) => void;
  onToggleSensor?: (sectionId: string, sensorName: string) => void;
  onAddSensor?: (sectionId: string, sensorName: string) => void;
  registerSensorToggleHandler?: (dataLayer: string, handler: (sectionId: string, sensorName: string) => void) => void;
  registerSensorData?: (
    dataLayer: string,
    enabledSensors: Record<string, boolean>,
    sensorColors: Record<string, string>
  ) => void;
  allEnabledSensors?: Record<string, Record<string, boolean>>;
  allSensorColors?: Record<string, Record<string, string>>;
  masterSensorColorMap?: Record<string, string>; // Global sensor name -> color mapping for consistency across all data layers
  legendSync?: LegendSyncReturn;
  focusedSensors?: Record<string, string>; // Map of sectionId -> focused sensor name
  onFocus?: (sensorName: string, sectionId?: string) => void;
  loadingSensors?: Set<string>;
  setLoadingSensor?: (sensorName: string) => void;
  clearLoadingSensor?: (sensorName: string) => void;
  verticalLinesState?: any; // Spectrum's own markers (for charts)
  onRemoveSpectrumMarker?: (markerId: string) => void; // Callback to remove Spectrum marker
  onClearAllSpectrumMarkers?: () => void; // Callback to clear all user markers from Spectrum
  sensorFusionVerticalLines?: any; // SensorFusion markers (for chips)
  onRemoveSensorFusionMarker?: (markerId: string) => void; // Callback to remove SensorFusion marker
  onClearAllSensorFusionMarkers?: () => void; // Callback to clear all user markers from SensorFusion
  selectedMarkerTimestamp?: string | null; // Currently selected marker for highlighting
  setSelectedMarkerTimestamp?: (timestamp: string | null) => void; // Set selected marker
  focusMode?: boolean; // Focus mode for multi-select timestamps
  setFocusMode?: (focusMode: boolean) => void; // Set focus mode
  selectedTimestampsSet?: Set<string>; // Selected timestamps in focus mode (immediate, for UI)
  setSelectedTimestampsSet?: (timestamps: Set<string> | ((prev: Set<string>) => Set<string>)) => void; // Set selected timestamps
  debouncedSelectedTimestampsSet?: Set<string>; // Debounced selected timestamps (for filter values and API calls)
  forceFilterRefresh?: () => void; // Force filter refresh (immediate)
  debouncedForceFilterRefresh?: () => void; // Debounced filter refresh (for multi-select)
  expandableLegendSeries?: any[]; // Expandable legend series for focus mode
  onToggleExpandableTimestamp?: (seriesId: string, timestampId: string) => void; // Toggle timestamp on/off
  onToggleExpandableSeries?: (seriesId: string) => void; // Toggle series selection
  onToggleExpandableSeriesFocus?: (seriesId: string) => void; // Toggle series focus/expand
  allowMultiTimestampMode?: boolean; // Control whether focus mode allows multiple timestamp selection (default: true)
  isTimestampsLoading?: boolean; // Whether timestamps data is currently loading (for chart loading in focus mode)
}): DataLayerConfig[] => {
  const {
    activeDataLayers,
    isMobileAsset,
    assetName,
    diagnostics,
    baseTitle,
    from,
    to,
    spectrumGraphs,
    spectrumAnalysisGraphs,
    spectrumManager,
    spectrumAnalysisManager,
    enhancedQueryParams,
    forceDataRefresh,
    handleSensorLoadingStateChange,
    selectedComponentId,
    selectedComponentObj,
    setTriggerSensorReset,
    verticalLinesState,
    sensorFusionVerticalLines,
    config,
    legendItemStates,
    toggleLegendItem,
    onToggleSensor,
    onAddSensor,
    registerSensorToggleHandler,
    registerSensorData,
    allEnabledSensors,
    allSensorColors,
    masterSensorColorMap,
    legendSync,
    focusedSensors,
    onFocus,
    loadingSensors,
    setLoadingSensor,
    clearLoadingSensor,
    selectedMarkerTimestamp,
    setSelectedMarkerTimestamp,
    onRemoveSpectrumMarker,
    onClearAllSpectrumMarkers,
    onRemoveSensorFusionMarker,
    onClearAllSensorFusionMarkers,
    focusMode,
    setFocusMode,
    selectedTimestampsSet,
    setSelectedTimestampsSet,
    debouncedSelectedTimestampsSet,
    forceFilterRefresh,
    debouncedForceFilterRefresh,
    expandableLegendSeries,
    onToggleExpandableTimestamp,
    onToggleExpandableSeries,
    onToggleExpandableSeriesFocus,
    allowMultiTimestampMode = true, // Default to true for backward compatibility
    isTimestampsLoading = false,
  } = props;

  return [
    {
      id: SENSOR_FUSION_DATA_LAYERS.SPECTRUM,
      condition: () => activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.SPECTRUM),
      component: () => (
        <BaseCard
          variant="wrapper"
          classNames={{
            card: 'flex flex-col w-full ',
            content: 'flex flex-col w-full relative p-0 bg-transparent',
          }}
          title={
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <CardTitle text={`${baseTitle} Sensors`} />
              </div>
            </div>
          }
        >
          Spectrum Trends
        </BaseCard>
      ),
    },
    {
      id: SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS,
      condition: () => activeDataLayers.includes(SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS),
      component: () => {
        // CRITICAL FIX: Use ref to track current focusMode value so handler always has access to it
        const focusModeRef = useRef(focusMode);
        useEffect(() => {
          focusModeRef.current = focusMode;
        }, [focusMode]);

        // Get filters from config for sensors data layer
        const dataLayer = config?.dataLayers?.find(
          (layer: any) => layer.id === SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS
        ) as DataLayerWithExpandableLegend;

        // Get expandable legend configuration - check root level first, then dataLayer
        // Backend can put expandable legend at config root OR inside dataLayer
        const expandableLegend: ExpandableLegend | undefined =
          config?.expandableLegend?.enabled ? config.expandableLegend :
          dataLayer?.expandableLegend;

        // CRITICAL: Memoize filter processing to prevent creating new arrays/objects on every render
        // This was causing querySignature changes and unwanted refetches
        // 
        // MAJOR IMPROVEMENT: With expandable legend, we eliminated the complex "latest" search logic
        // that previously required searching through filter options (lines 203-211 in old version)
        // Now we can directly access expandable_legend type filters without complex searching
        const { processedFilters, chipFilterIds, backendTimestampFilter } = useMemo(() => {
          const filters = transformFilters(dataLayer?.filters || []);

          // CRITICAL: In Spectrum, ALL chips filters should be single-select
          // Override any backend chips filters to enforce single-select behavior
          // AND store references to all chip filter IDs for mutual exclusivity
          const chipIds: string[] = [];
          filters.forEach((filter) => {
            if (filter.type === 'chips') {
              filter.isMultiSelect = false;
              chipIds.push(filter.id);
            }
          });

          // Find backend chip filter that contains timestamps
          // NEW APPROACH: Look for expandable_legend type first, fallback to chips search
          let timestampFilter;
          
          if (isExpandableLegendEnabled()) {
            // Use new expandable legend approach - look for expandable_legend type filter
            timestampFilter = filters.find((f) => f.type === 'expandable_legend');
          }
          
          // Fallback to old complex search logic if needed
          if (!timestampFilter) {
            timestampFilter = filters.find((f) => {
              if (f.type === 'chips') {
                const hasLatest = f.options?.some(
                  (opt: any) => opt.label?.toLowerCase().includes('latest') || opt.value === '__latest__'
                );
                return hasLatest;
              }
              return false;
            });
          }

          return {
            processedFilters: filters,
            chipFilterIds: chipIds,
            backendTimestampFilter: timestampFilter,
          };
        }, [
          dataLayer?.filters,
          // REMOVED: sensorFusionVerticalLines?.verticalLines?.length - filter mutations handle markers dynamically
          debouncedSelectedTimestampsSet,
          focusMode
        ]);

        // ADAPTER: Add focused flag to legend items for spectrum
        // Backend doesn't support multiple timestamps with multiple sensors
        // Add focused: true to the focused sensor's legend entry so backend can handle it
        // CRITICAL: Memoize to prevent creating new array reference on every render
        // Only recreate when spectrumAnalysisGraphs or focusedSensors actually change
        // NOTE: expandableLegend is NOT included here to avoid triggering signature changes on timestamp selection
        const graphsWithLegendState = useMemo(() => {
          // SPECTRUM-SPECIFIC: In Spectrum, legends are synced across ALL sections
          // So we use ANY focused sensor from ANY section (not section-specific)
          const anyFocusedSensor = focusedSensors ? Object.values(focusedSensors).find(s => s) : undefined;

          return spectrumAnalysisGraphs.map((graph: any) => {
            if (!graph.legend) {
              return graph;
            }

            // Apply focused state to ALL sections based on ANY focused sensor
            // This ensures consistent legend state across all sections (Resolution, Envelopes, etc.)
            return {
              ...graph,
              legend: graph.legend.map((legendItem: any) => {
                const isFocused = anyFocusedSensor &&
                  (legendItem.displayName === anyFocusedSensor || legendItem.name === anyFocusedSensor);
                return {
                  ...legendItem,
                  focused: !!isFocused,  // Always add focused property
                };
              }),
            };
          });
        }, [spectrumAnalysisGraphs, focusedSensors]);

        // Build expandableLegend with current enabled states for API body
        // This is separate from graphsWithLegendState to avoid triggering signature recalculation
        // on every timestamp selection change
        const expandableLegendForApi = useMemo((): ExpandableLegend | undefined => {
          if (!isExpandableLegendEnabled() || !expandableLegend?.enabled) {
            return undefined;
          }

          const backendItems = expandableLegend?.items || [];
          const sensorFusionLines = sensorFusionVerticalLines?.verticalLines || [];

          // Merge backend items with user-added markers from SensorFusion
          const allItems = mergeExpandableLegendItemsWithVerticalLines(
            backendItems,
            sensorFusionLines,
            expandableLegend
          );

          // Pre-compute selected item IDs from debouncedSelectedTimestampsSet
          // The set might contain either item IDs or timestamp values, so we need to resolve them
          const selectedItemIdsForApi = new Set(
            Array.from(debouncedSelectedTimestampsSet || []).map(selectedValue => {
              // Find the matching item by either id or value
              const matchingItem = allItems.find(item =>
                item.id === selectedValue || item.value === selectedValue
              );
              // Return the item's ID if found, otherwise return the original value
              return matchingItem ? matchingItem.id : selectedValue;
            })
          );

          // Update enabled state based on resolved selected item IDs
          const itemsWithEnabledState = allItems.map(item => ({
            ...item,
            enabled: selectedItemIdsForApi.has(item.id)
          }));

          return {
            ...expandableLegend,
            items: itemsWithEnabledState,
            multiSelect: !!(focusMode && allowMultiTimestampMode)
          };
        }, [expandableLegend, debouncedSelectedTimestampsSet, sensorFusionVerticalLines?.verticalLines, focusMode, allowMultiTimestampMode]);

        // Create search options from actual sensor data (like SensorFusion mode)
        const spectrumAnalysisDataLayerData = allEnabledSensors?.[SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS] || {};
        const spectrumAnalysisColorData = allSensorColors?.[SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS] || {};

        // Create search options with section information
        const searchOptions: any[] = [];

        // Go through each graph to find which section each sensor belongs to
        spectrumAnalysisGraphs.forEach((graph: any) => {
          const sectionTitle = graph.title || graph.id;

          // Check for sensors in this graph's legend or sensorUiNames
          const sensorNames =
            graph.sensorUiNames || graph.legend?.map((item: any) => item.displayName || item.name) || [];

          sensorNames.forEach((sensorName: string) => {
            if (Object.prototype.hasOwnProperty.call(spectrumAnalysisDataLayerData, sensorName)) {
              searchOptions.push({
                value: sensorName,
                label: sensorName,
                unit: graph.unit || '',
                enabled: spectrumAnalysisDataLayerData[sensorName],
                color: spectrumAnalysisColorData[sensorName],
                section: sectionTitle, // Display name for UI
                sectionId: graph.id, // Graph ID for toggle handlers - CRITICAL!
              });
            }
          });
        });

        // If backend provides a search filter with useLocalLegend, populate it with sensor data
        const backendSearchFilter = processedFilters.find((f) => f.type === 'search' && f.searchConfig?.useLocalLegend);
        if (backendSearchFilter && searchOptions.length > 0) {
          // Update the backend search filter with actual sensor options (DIRECT MUTATION like SensorFusion)
          backendSearchFilter.options = searchOptions;
        } else if (backendSearchFilter && searchOptions.length === 0) {
        }

        // CRITICAL: Memoize sorted markers to prevent reprocessing on every render
        // Only reprocess when marker counts actually change
        const sortedMarkers = useMemo(() => {
          // Add user markers from both SensorFusion and Spectrum
          const sensorFusionUserMarkers = sensorFusionVerticalLines?.verticalLines
            ? sensorFusionVerticalLines.verticalLines
                .filter((line: any) => line.markerType === 'user' && !line.isHarmonic)
                .map((line: any) => ({ ...line, source: 'sensorFusion' }))
            : [];

          const spectrumUserMarkers = verticalLinesState?.verticalLines
            ? verticalLinesState.verticalLines
                .filter((line: any) => line.markerType === 'user' && !line.isHarmonic)
                .map((line: any) => ({ ...line, source: 'spectrum' }))
            : [];

          // Combine and sort markers by timestamp
          return [...sensorFusionUserMarkers, ...spectrumUserMarkers].sort((a: any, b: any) => {
            const aTime = a.timestamp instanceof Date ? a.timestamp.getTime() : Number(a.timestamp) || 0;
            const bTime = b.timestamp instanceof Date ? b.timestamp.getTime() : Number(b.timestamp) || 0;
            return aTime - bTime;
          });
        }, [
          sensorFusionVerticalLines?.verticalLines?.length,
          verticalLinesState?.verticalLines?.length
        ]);

        if (backendTimestampFilter) {
          // NEW APPROACH: For expandable_legend type, less complex setup needed
          if (isExpandableLegendEnabled() && expandableLegend?.enabled && backendTimestampFilter.type === 'expandable_legend') {
            // Expandable legend filter - simplified setup
            (backendTimestampFilter as any).hidden = true;
            (backendTimestampFilter as any).isMultiSelect = focusMode && allowMultiTimestampMode;
          } else {
            // FALLBACK: Original complex setup for chips filter
            // Mark filter as hidden so it won't display chips but will still process handlers
            (backendTimestampFilter as any).hidden = true;
            (backendTimestampFilter as any).chipDisplayMode = 'carousel' as const;
            // Enable multi-select ONLY if both focusMode is active AND allowMultiTimestampMode is true
            // When allowMultiTimestampMode=false, focus mode behaves like sensor focus (opacity-based, single selection)
            // When allowMultiTimestampMode=true, focus mode allows multiple timestamps (with expandable sub-rows)
            // IMPORTANT: Use focusMode directly (not focusModeRef) because this runs during render,
            // and the ref is only updated in useEffect which runs AFTER render
            (backendTimestampFilter as any).isMultiSelect = focusMode && allowMultiTimestampMode;
          }

          // NEW APPROACH: For expandable legend, options come directly from backend
          // FALLBACK: Create marker options for old approach
          let markerOptions: any[] = [];
          
          if (!isExpandableLegendEnabled() || !expandableLegend?.enabled || backendTimestampFilter.type !== 'expandable_legend') {
            // Only create marker options for old chips approach
            markerOptions = sortedMarkers.map((line: any) => {
              let displayLabel = line.label;
              if (line.timestamp && line.timestamp instanceof Date) {
                const formatter = new Intl.DateTimeFormat('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                });
                displayLabel = `${line.label}: ${formatter.format(line.timestamp)}`;
              }

              const optionValue = line.timestamp
                ? line.timestamp instanceof Date
                  ? line.timestamp.toISOString()
                  : String(line.timestamp)
                : line.id;

              const isEnabled = debouncedSelectedTimestampsSet?.has(optionValue) || false;

              return {
                value: optionValue,
                label: displayLabel,
                enabled: isEnabled,
                color: line.color,
                badge: 'user' as const,
                source: line.source || 'sensorFusion', // Use the actual source (sensorFusion or spectrum)
                markerSource: line.source, // Store marker source for deletion routing
                markerId: line.id, // Store the actual line ID for deletion
                timestamp: line.timestamp,
                canDelete: true,
              };
            });
          }

          // NEW APPROACH: For expandable legend, options come from backend directly
          // FALLBACK: Process backend options for old approach
          if (isExpandableLegendEnabled() && expandableLegend?.enabled && backendTimestampFilter.type === 'expandable_legend') {
            // For expandable legend, options are managed differently
            // The backend provides the complete expandableLegend structure
            // We don't need to manually merge marker options
          } else {
            // FALLBACK: Original complex options processing
            // Get backend options (Latest)
            const originalBackendOptions = (backendTimestampFilter.options || []).filter((opt: any) => {
              return opt.source !== 'user' && opt.badge !== 'user';
            });

            const backendOptions = originalBackendOptions.map((opt: any) => {
              const isLatestOption = opt.value === '__latest__' || opt.value === 'latest' || opt.value === '0';
              let enabled: boolean;

              if (isLatestOption) {
                if (focusModeRef.current) {
                  const hasLatestInDebouncedSet = debouncedSelectedTimestampsSet?.has(opt.value) ||
                                                   debouncedSelectedTimestampsSet?.has('__latest__') ||
                                                   debouncedSelectedTimestampsSet?.has('latest') ||
                                                   debouncedSelectedTimestampsSet?.has('0');
                  enabled = hasLatestInDebouncedSet || !debouncedSelectedTimestampsSet || debouncedSelectedTimestampsSet.size === 0;
                } else {
                  enabled = !debouncedSelectedTimestampsSet || debouncedSelectedTimestampsSet.size === 0;
                }
              } else {
                enabled = debouncedSelectedTimestampsSet?.has(opt.value) || false;
              }

              return { ...opt, enabled };
            });

            backendTimestampFilter.options = [...markerOptions, ...backendOptions];
          }

          // Set defaultValue - simplified for new approach
          if (isExpandableLegendEnabled() && expandableLegend?.enabled && backendTimestampFilter.type === 'expandable_legend') {
            // NEW APPROACH: Default value comes from expandable legend defaultSelection
            if (expandableLegend.defaultSelection && expandableLegend.defaultSelection.length > 0) {
              const defaultItem = expandableLegend.items.find((item: any) => item.id === expandableLegend.defaultSelection[0]);
              if (defaultItem) {
                backendTimestampFilter.defaultValue = defaultItem.value;
              }
            }
          } else {
            // FALLBACK: Original defaultValue logic
            if (debouncedSelectedTimestampsSet && debouncedSelectedTimestampsSet.size > 0) {
              const firstValue = Array.from(debouncedSelectedTimestampsSet)[0];
              const matchingOption = backendTimestampFilter.options?.find((opt: any) => opt.value === firstValue);
              if (matchingOption) {
                backendTimestampFilter.defaultValue = firstValue;
              }
            }

            if (!backendTimestampFilter.defaultValue) {
              const firstEnabledOption = backendTimestampFilter.options?.find((opt: any) => opt.enabled);
              if (firstEnabledOption) {
                backendTimestampFilter.defaultValue = firstEnabledOption.value;
              }
            }
          }

          const originalHandler = backendTimestampFilter.handler;

          // Delete handler - supports both SensorFusion and Spectrum markers
          backendTimestampFilter.onDelete = (onRemoveSensorFusionMarker || onRemoveSpectrumMarker)
            ? (optionValue: string) => {
                const marker = sortedMarkers.find((line: any) => {
                  const timestampValue = line.timestamp instanceof Date ? line.timestamp.toISOString() : String(line.timestamp);
                  return timestampValue === optionValue || line.id === optionValue;
                });

                if (marker) {
                  if (setSelectedTimestampsSet && selectedTimestampsSet?.has(optionValue)) {
                    setSelectedTimestampsSet((prev) => {
                      const newSet = new Set(prev);
                      newSet.delete(optionValue);
                      return newSet;
                    });

                    if (originalHandler && selectedTimestampsSet) {
                      const remainingTimestamps = Array.from(selectedTimestampsSet).filter((t) => t !== optionValue);
                      if (remainingTimestamps.length > 0) {
                        originalHandler(remainingTimestamps, backendTimestampFilter);
                      } else {
                        originalHandler(['__latest__'], backendTimestampFilter);
                      }
                    }
                  }

                  if (setSelectedMarkerTimestamp && selectedMarkerTimestamp === optionValue) {
                    setSelectedMarkerTimestamp(null);
                  }

                  // Delete from the correct source
                  if (marker.source === 'sensorFusion' && onRemoveSensorFusionMarker) {
                    onRemoveSensorFusionMarker(marker.id);
                  } else if (marker.source === 'spectrum' && onRemoveSpectrumMarker) {
                    onRemoveSpectrumMarker(marker.id);
                  }
                }
              }
            : undefined;

          backendTimestampFilter.handler = (value: string | string[]) => {
            const values = Array.isArray(value) ? value : [value];
            backendTimestampFilter.defaultValue = values[0] || undefined;

            // CRITICAL: Update selectedTimestampsSet to trigger timestampParam change
            // This propagates up to NewSpectrum which increments forceDataRefresh
            // Without this, selecting a timestamp wouldn't trigger an API refetch
            if (setSelectedTimestampsSet) {
              setSelectedTimestampsSet(new Set(values));
            }
          };

          chipFilterIds.push(backendTimestampFilter.id);
        }

        // Generate expandableLegendSeries from chip filter options for popover
        // CRITICAL: Memoize to prevent creating new array on every render
        const popoverExpandableLegendSeries = useMemo(() => {
          // NEW APPROACH: Use expandable legend items if available
          if (isExpandableLegendEnabled() && expandableLegend?.enabled) {
            // Use items from expandableLegend merged with user markers from SensorFusion
            const backendItems = expandableLegend?.items || [];
            const sensorFusionLines = sensorFusionVerticalLines?.verticalLines || [];

            // Merge backend items with user-added markers from SensorFusion (bidirectional sync)
            const allItems = mergeExpandableLegendItemsWithVerticalLines(
              backendItems,
              sensorFusionLines,
              expandableLegend
            );

            const selectedIds = new Set(
              Array.from(selectedTimestampsSet || []).map(timestamp => {
                // Find item by timestamp value OR by item id (handler stores id, not value)
                const item = allItems.find(item => item.value === timestamp || item.id === timestamp);
                return item ? item.id : timestamp;
              })
            );

            return convertItemsToPopoverFormat(allItems, selectedIds);
          }
          
          // FALLBACK: Use old chip filter approach
          return backendTimestampFilter?.options?.map((option: any) => {
            const isLatestOption = option.value === '__latest__' || option.value === 'latest' || option.value === '0';
            let isSelected: boolean;

            if (isLatestOption) {
              const hasLatestInSet = selectedTimestampsSet?.has(option.value) ||
                                     selectedTimestampsSet?.has('__latest__') ||
                                     selectedTimestampsSet?.has('latest') ||
                                     selectedTimestampsSet?.has('0');
              isSelected = hasLatestInSet || !selectedTimestampsSet || selectedTimestampsSet.size === 0;
            } else {
              isSelected = selectedTimestampsSet?.has(option.value) ?? false;
            }

            return {
              id: option.value,
              label: option.label,
              color: option.color || '#6667dd',
              isSelected,
              isFocused: false,
              timestamps: [],
              badge: option.badge,
              source: option.source,
              canDelete: option.canDelete,
              markerId: option.markerId, // Pass through the marker ID for deletion
              markerSource: option.markerSource, // Pass through marker source for deletion routing
              onDelete: option.canDelete && option.markerId
                ? () => {
                    // Route to correct delete handler based on marker source
                    if (option.markerSource === 'spectrum' && onRemoveSpectrumMarker) {
                      onRemoveSpectrumMarker(option.markerId);
                    } else if (option.markerSource === 'sensorFusion' && onRemoveSensorFusionMarker) {
                      onRemoveSensorFusionMarker(option.markerId);
                    }
                  }
                : undefined,
            };
          }) || [];
        }, [isExpandableLegendEnabled(), expandableLegend, dataLayer, backendTimestampFilter?.options, onRemoveSensorFusionMarker, onRemoveSpectrumMarker, selectedTimestampsSet, sensorFusionVerticalLines?.verticalLines]);

        return (
          <ManagedGraphsSection
            title={`${baseTitle} Spectrum Analysis`}
            dataLayer={SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS}
            graphs={graphsWithLegendState}
            selectedSections={spectrumAnalysisManager.selectedSections}
            onSelectionChange={spectrumAnalysisManager.handleSelectionChange}
            showReset={true}
            syncSectionLegends={true}
            syncMarking={true}
            hideManagedGraphsDropdown={true}
            enableCompareTable={false}
            enableMarkers={true}
            enableHarmonics={true}
            enableSideBands={true}
            enableDrawing={true}
            enableHarmonicEdit={false}
            enableSideBandsEdit={false}
            expandableLegendLabel="Timestamps"
            showFloatingToolbar={false}
            autoscaleMode="onDataLoad"
            autoscaleAxis="both"
            showYAxisScale={true}
            enableDynamicMargin={true}
            triggerReset={spectrumAnalysisManager.triggerReset}
            onReset={() => {
              setTriggerSensorReset(true);
              spectrumAnalysisManager.handleReset();
              // Clear all SensorFusion user markers (chips)
              if (onClearAllSensorFusionMarkers) {
                onClearAllSensorFusionMarkers();
              }
              // Clear selected marker timestamp
              if (setSelectedMarkerTimestamp) {
                setSelectedMarkerTimestamp(null);
              }
              // Container handles sensor reset automatically
            }}
            onResetDataLayer={() => {
              spectrumAnalysisManager.handleDataLayerReset();
              setTriggerSensorReset(false);
            }}
            loadingState={spectrumAnalysisManager.loadingState || isTimestampsLoading || (loadingSensors && loadingSensors.size > 0)}
            queryParams={(() => {
              const params = { ...enhancedQueryParams, data_layer: SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS };
              return params;
            })()}
            forceRefresh={forceDataRefresh}
            onLoadingStateChange={(isLoading) => {
              spectrumAnalysisManager.handleLoadingStateChange(isLoading);
              handleSensorLoadingStateChange(isLoading);
            }}
            includeAlarms={false}
            variant="lines"
            verticalLinesState={verticalLinesState}
            // Filter out chip filters from header - timestamp selection is via legend dropdown only
            filters={processedFilters.filter((f: any) => f.type !== 'chips')}
            mutuallyExclusiveChipFilters={chipFilterIds}
            selectedTimestampsSet={selectedTimestampsSet}
            onToggleSensor={onToggleSensor}
            onAddSensor={onAddSensor}
            registerSensorToggleHandler={registerSensorToggleHandler}
            registerSensorData={registerSensorData}
            sectionEnabledSensors={{ sensors: allEnabledSensors?.[SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS] || {} }}
            sectionSensorColors={{ sensors: allSensorColors?.[SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS] || {} }}
            masterSensorColorMap={masterSensorColorMap}
            isSensorLoading={(sectionId, sensorName) => {
              // Check if this sensor is in the global loading set
              return loadingSensors?.has(sensorName) || false;
            }}
            // Show inline Focus button in Spectrum for multi-timestamp selection
            showInlineFocusButton={true}
            onFilterChange={(filterId, value) => {
              // General filter change handler for sensors

              // CRITICAL: Chips Mutual Exclusivity
              // When any chip filter is changed, clear ALL OTHER chip filters
              // This ensures only ONE chip is selected across ALL chip filters
              if (chipFilterIds.includes(filterId)) {
                // If a chip was selected (value has items), clear all other chip filters
                if (Array.isArray(value) && value.length > 0) {
                  // This will be handled by DataLayerFilters through the mutuallyExclusiveGroups prop
                }
              }

              // The search filter handles sensor toggling through onToggleSensor/onAddSensor
            }}
            // Legend sync mode props - mode only, state handled by container
            legendSyncMode={legendSync?.mode}
            // Pass focus props so Eye icon appears on hover in legend
            focusedSensors={focusedSensors}
            onFocus={onFocus}
            onToggleFocusMode={legendSync?.toggleMode}
            // Global loading sensors state - synchronized across all sections
            loadingSensors={loadingSensors}
            setLoadingSensor={setLoadingSensor}
            clearLoadingSensor={clearLoadingSensor}
            selectedMarkerTimestamp={selectedMarkerTimestamp}
            expandableLegendSeries={expandableLegendSeries || []}
            expandableLegendFocusMode={(() => {
              const result = focusMode && allowMultiTimestampMode;
              // console.log('[data-layers-config] focusMode:', focusMode, '| allowMultiTimestampMode:', allowMultiTimestampMode, '| expandableLegendFocusMode:', result);
              return result;
            })()}
            popoverTimestamps={popoverExpandableLegendSeries}
            onToggleExpandableSeries={onToggleExpandableSeries || ((seriesId: string) => {
              if (!setSelectedTimestampsSet) return;

              // Only allow multi-select if BOTH focusMode is active AND allowMultiTimestampMode is true
              if (focusModeRef.current && allowMultiTimestampMode) {
                // Focus mode with multi-timestamp enabled: Multi-select - toggle in/out of set
                let updatedSet: Set<string>;
                setSelectedTimestampsSet((prev) => {
                  const newSet = new Set(prev);

                  // Check if this is Latest (could be '__latest__', 'latest', or '0')
                  const isLatest = seriesId === '__latest__' || seriesId === 'latest' || seriesId === '0';

                  if (isLatest) {
                    // For Latest, check if ANY of its possible values are in the set
                    const hasLatest = newSet.has('__latest__') || newSet.has('latest') || newSet.has('0');
                    if (hasLatest) {
                      // Remove all possible Latest values
                      newSet.delete('__latest__');
                      newSet.delete('latest');
                      newSet.delete('0');
                    } else {
                      // Add the seriesId value (whichever one was clicked)
                      newSet.add(seriesId);
                    }
                  } else {
                    // Toggle the clicked item - allow selecting both "Latest" and specific timestamps
                    // IMPORTANT: If the set is empty, it means Latest was selected in normal mode
                    // We need to preserve Latest when adding a timestamp in focus mode
                    if (newSet.size === 0) {
                      newSet.add('__latest__');
                      newSet.add('latest');
                      newSet.add('0');
                    }

                    if (newSet.has(seriesId)) {
                      newSet.delete(seriesId);
                    } else {
                      newSet.add(seriesId);
                    }
                  }

                  updatedSet = newSet; // Capture for handler
                  return newSet;
                });

                // DON'T call handler here - it will be called after debounce
              } else {
                // Normal mode: Single-select - replace entire set
                // For "Latest" in single-select, clear the set (no specific timestamp)
                const isLatest = seriesId === '__latest__' || seriesId === 'latest' || seriesId === '0';

                if (isLatest) {
                  setSelectedTimestampsSet(new Set());
                } else {
                  setSelectedTimestampsSet(new Set([seriesId]));
                }

                // DON'T call handler here - let debounce handle it
              }

              // DON'T force refresh here - selectedTimestampsSet change will trigger debounce
            })}
            onToggleExpandableSeriesFocus={onToggleExpandableSeriesFocus || ((seriesId: string) => {
              // When clicking Focus on a timestamp chip, enter focus mode and select that timestamp
              if (!setSelectedTimestampsSet || !setFocusMode) return;

              // Enter focus mode
              setFocusMode(true);

              // Add this timestamp to the selected set (if not already there)
              // IMPORTANT: If the set was empty (meaning Latest was selected in normal mode),
              // include Latest ('0') in the new set so it stays selected
              let updatedValues: string[] = [];
              setSelectedTimestampsSet((prev) => {
                const newSet = new Set(prev);

                // If coming from normal mode with Latest selected (empty set),
                // add Latest ('0') to the set to keep it selected
                if (prev.size === 0 && !focusModeRef.current) {
                  newSet.add('0'); // Latest value
                }

                newSet.add(seriesId);
                updatedValues = Array.from(newSet);
                return newSet;
              });

              // Call the filter handler to update backend with the new values
              if (backendTimestampFilter?.handler) {
                backendTimestampFilter.handler(updatedValues, backendTimestampFilter);
              }

              // Force filter refresh to update UI - use immediate refresh for focus entry (single action)
              if (forceFilterRefresh) {
                forceFilterRefresh();
              }
            })}
            onToggleExpandableTimestamp={onToggleExpandableTimestamp || ((seriesId: string, timestampId: string) => {
              // Use the generic callback from NewSpectrum
            })}
            onRemoveMarker={(markerId: string) => {
              // markerId is the actual vertical line ID (e.g., "line-123456-abc")
              // Find the option by markerId, then get its value for deletion
              const option = backendTimestampFilter?.options?.find((opt: any) => opt.markerId === markerId);

              if (option && backendTimestampFilter?.onDelete) {
                backendTimestampFilter.onDelete(option.value);
              }
            }}
            expandableLegendForApi={expandableLegendForApi}
          />
        );
      },
    },
  ];
};

/**
 * Render order for data layers
 */
export const DATA_LAYERS_RENDER_ORDER = [
  SENSOR_FUSION_DATA_LAYERS.SPECTRUM,
  SENSOR_FUSION_DATA_LAYERS.SPECTRUM_ANALYSIS,
];
